import React from 'react';
import { AlertTriangle, Inbox, RotateCw } from 'lucide-react';
import Button from './Button';

/** Deliberate empty state: what is missing, why, and the way forward. */
export const EmptyState = ({
    icon: Icon = Inbox,
    title = 'Nothing here yet',
    description,
    action,
    compact = false,
    className = '',
}) => (
    <div className={`text-center ${compact ? 'py-8 px-4' : 'py-14 px-6'} ${className}`}>
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-ink-100 text-ink-500 mb-3">
            <Icon size={18} aria-hidden="true" />
        </span>
        <h3 className="text-md font-semibold text-ink-800">{title}</h3>
        {description && (
            <p className="text-base text-ink-500 mt-1 max-w-[46ch] mx-auto leading-relaxed">
                {description}
            </p>
        )}
        {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
);

/** Inline failure: what happened, what to do, and a retry when one exists. */
export const ErrorState = ({
    title = 'Something went wrong',
    description = 'The data could not be loaded. Check your connection and try again.',
    onRetry,
    compact = false,
    className = '',
}) => (
    <div className={`text-center ${compact ? 'py-8 px-4' : 'py-12 px-6'} ${className}`}>
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-danger-subtle text-danger mb-3">
            <AlertTriangle size={18} aria-hidden="true" />
        </span>
        <h3 className="text-md font-semibold text-ink-800">{title}</h3>
        <p className="text-base text-ink-500 mt-1 max-w-[46ch] mx-auto leading-relaxed">
            {description}
        </p>
        {onRetry && (
            <div className="mt-4 flex justify-center">
                <Button variant="secondary" size="sm" icon={RotateCw} onClick={onRetry}>
                    Try again
                </Button>
            </div>
        )}
    </div>
);

/** Inline alert used inside forms and detail panes. */
export const Alert = ({ variant = 'info', title, children, className = '' }) => {
    const tones = {
        info: 'bg-info-subtle text-info-strong',
        success: 'bg-success-subtle text-success-strong',
        warning: 'bg-warning-subtle text-warning-strong',
        danger: 'bg-danger-subtle text-danger-strong',
    };
    return (
        <div
            role={variant === 'danger' ? 'alert' : 'status'}
            className={`rounded-md px-3.5 py-2.5 text-base ${tones[variant]} ${className}`}
        >
            {title && <p className="font-semibold mb-0.5">{title}</p>}
            {children}
        </div>
    );
};

export default EmptyState;
